// FNP-1193 — the certificate read path, plus the connection capability
// snapshot the write controls depend on.

// ⚠️ `status`, `statusDetail` AND `expirationDate` ARE SELECTED ON EVERY READ,
// and that is not redundancy — it is the design.
//
// None of the three is STORED server-side. They are read through to the
// provider on every request (FNP-1221's central decision), which is what keeps
// a certificate's status from disagreeing with the provider that owns it. A
// caller that selected them once and cached the result would reintroduce
// exactly the staleness the design pays an N+1 to avoid.
//
// ⚠️ `validationRecordWrittenAt` IS SELECTED EVEN THOUGH NOTHING RENDERS IT
// DIRECTLY. It is the clock STUCK is measured from, and its NULLNESS is what
// separates VALIDATION_NOT_WRITTEN from STUCK. The backend computes the status
// from it, but the console shows the operator *why* a certificate is in the
// state it is in, and "Flexi never recorded writing the record" is that reason.
//
// ⚠️ `validationRecords` IS SELECTED FOR THE LIST, not only the detail. When
// validation is STUCK or was never written, the record is the first thing
// anyone wants to compare against the zone — making them open a second view to
// see it is the friction this module exists to remove.
const CERTIFICATE_FIELDS = `
    id
    accountId
    connectionId
    domainName
    coveredNames
    targetType
    region
    status
    statusDetail
    expirationDate
    validationRecords {
        name
        type
        value
        domainName
        status
    }
    validationRecordWrittenAt
    validationZoneId
    stuckAfterMinutes
    issuingAwsAccountId
    createdByUsername
    created
    updated
`;

// ⚠️ THERE IS DELIBERATELY NO `accountId` ARGUMENT. The tenant is a KEY
// CONDITION on the server, built from the caller's own claim — not a filter the
// client supplies. Another tenant's certificates are in a different partition
// and are never read. Sending one would be a field the schema does not have.
export const listCertificates = `
   query listCertificates($limit: Int, $nextToken: String) {
       listCertificates(limit: $limit, nextToken: $nextToken) {
           items {${CERTIFICATE_FIELDS}}
           nextToken
       }
   }
`;

// ⚠️ THE `id` IS NOT TRUSTED BY THE SERVER EITHER. The caller's tenant comes
// from their claim and the id must begin with that tenant's segment, checked
// before any storage read. A caller who learns another tenant's certificate id
// cannot use it.
export const getCertificate = `
   query getCertificate($id: ID!) {
       getCertificate(id: $id) {${CERTIFICATE_FIELDS}}
   }
`;

// The connections a certificate can be requested through.
//
// ⚠️ FNP-1280 — THE GATE IS `certificateCapabilities.canRequest`, AND IT USED
// TO BE `writable`. That was the defect.
//
// `writable` is the DNS declaration: the tenant granted
// `route53:ChangeResourceRecordSets` and the zone-lifecycle actions. The
// backend used to derive its certificate gate from it and nothing else —
// `flexi-domains-api/src/certificate/src/main.ts` read
// `connection.writable === true` ONCE and handed the single answer to both
// `canRequest` and `canDelete`. But a tenant writes the ACM grants as
// SEPARATE statements in the same role, so a tenant with DNS write and no ACM
// write passed this gate and then failed at ACM after filling in the form.
//
// The connection now carries its own `certificateCapabilities` block,
// mirroring the issuer port rather than the DNS one, and this selects it. So
// the console hides exactly the controls the backend would refuse, with no
// proxy in between.
//
// ⚠️ NOT `capabilities`, AND NOT `canWriteRecords`. The natural guess is that
// requesting a certificate writes a validation record, so `canWriteRecords`
// must govern it. It does not: that block is a mirror of the DNS port and
// carries no certificate field at all. Reaching for it would be a proxy for
// the wrong grant.
//
// ⚠️ IT STILL DOES NOT PROVE THE REQUEST WILL SUCCEED, and this module never
// claims it does. The declaration is made BY THE TENANT, not probed — probing
// needs `iam:SimulatePrincipalPolicy` evaluated in the account owning the
// principal, which is the tenant's, so Flexi cannot verify it. A tenant who
// declares `canRequest: true` on a role that lacks the grant passes this gate
// and still fails at ACM with AccessDenied. That refusal arrives classified
// PERMISSION_DENIED and gets copy naming the action; see writeErrorMessages.
export const listProviderConnectionsForCertificates = `
   query listProviderConnections($limit: Int, $nextToken: String) {
       listProviderConnections(limit: $limit, nextToken: $nextToken) {
           items {
               id
               displayName
               providerId
               status
               writable
               certificateCapabilities {
                   canRequest
                   canDelete
               }
           }
           nextToken
       }
   }
`;

// One connection's write declaration, for the hiding decision on a page that
// already knows which connection it is asking about.
//
// `secretRef` is deliberately NOT selected: this module never needs it, and not
// asking is one fewer place it could be logged. `capabilities` is not selected
// either — none of its five fields governs certificates, which is exactly why
// FNP-1280 put the certificate declaration in its own sibling block.
//
// `writable` IS still selected, and not as a leftover: the request flow also
// PUBLISHES a validation record through the same connection's DNS credential,
// so a connection that can issue but cannot write DNS produces a certificate
// stuck in VALIDATION_NOT_WRITTEN. The two are reported separately because
// they now genuinely are separate.
export const getProviderConnectionCapabilities = `
   query getProviderConnection($id: ID!) {
       getProviderConnection(id: $id) {
           id
           status
           writable
           certificateCapabilities {
               canRequest
               canDelete
           }
       }
   }
`;
